import React from 'react';
import { TaskyConfirmDialog } from './TaskyConfirmDialog';

export interface ConfirmOptions {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** 既定 'warning' */
  tone?: 'destructive' | 'warning' | 'info';
  /** 既定 'OK' */
  confirmText?: string;
  /** 既定 'キャンセル' */
  cancelText?: string;
  /** true ならキャンセルボタンを出さない（OK だけの確認。旧 alert の置き換え用） */
  hideCancel?: boolean;
}

type ConfirmFn = (options: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

/** Provider 外のフォールバック警告は 1 回だけ出す。 */
let warnedOutsideProvider = false;

/** window.confirm に渡す平文。title と description を改行で繋ぐ。 */
const toPlainText = (options: ConfirmOptions | string): string => {
  if (typeof options === 'string') return options;
  const title =
    typeof options.title === 'string' && options.title.trim() !== '' ? options.title : '確認';
  const description = typeof options.description === 'string' ? options.description : '';
  return description ? `${title}\n${description}` : title;
};

/** 文字列引数は 1 行目を title、残りを description として扱う。 */
const normalize = (options: ConfirmOptions | string): ConfirmOptions => {
  if (typeof options !== 'string') return options;
  const [title, ...rest] = options.split('\n');
  const description = rest.join('\n').trim();
  return description ? { title, description } : { title };
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

  const settle = React.useCallback((value: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setOptions(null);
    resolve?.(value);
  }, []);

  const confirm = React.useCallback<ConfirmFn>(
    (input) => {
      // 前のダイアログが残っていたら（多重呼び出し）キャンセル扱いで閉じる。
      resolveRef.current?.(false);
      setOptions(normalize(input));
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    []
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <TaskyConfirmDialog
        open={options !== null}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        tone={options?.tone ?? 'warning'}
        title={options?.title ?? ''}
        description={options?.description}
        confirmText={options?.confirmText ?? 'OK'}
        cancelText={options?.cancelText ?? 'キャンセル'}
        hideCancel={options?.hideCancel ?? false}
      />
    </ConfirmContext.Provider>
  );
};

/**
 * window.confirm の置き換え。true = 確定、false = キャンセル／閉じる。
 *
 * ConfirmProvider の外で呼ばれた場合は throw せず window.confirm にフォールバックする
 * （Provider で包んでいない既存ページ・既存テストを白画面にしないため）。
 */
export function useConfirm(): ConfirmFn {
  const confirm = React.useContext(ConfirmContext);
  const fallback = React.useCallback<ConfirmFn>((options) => {
    if (process.env.NODE_ENV !== 'production' && !warnedOutsideProvider) {
      warnedOutsideProvider = true;
      console.warn(
        '[useConfirm] ConfirmProvider の外で呼ばれました。window.confirm にフォールバックします'
      );
    }
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(toPlainText(options)));
  }, []);

  return confirm ?? fallback;
}
